import React, {useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';
import {RootState} from '../../../redux/store.type';
import useShow from '../../../hooks/useShow';
import useOnClickOutside from '../../../hooks/UseOnClickOutside';
import {useIsSubscription} from '../hooks/useIsSubscription';
import {getSubscriptionVenues} from '../MemberTicket/memberTicket.utils';
import {getFabMenu} from './fabMenu';
import MenuItem from './MenuItem';
import ContactSheet from '../components/ContactSheet/ContactSheet';
import NavigationSheet from '../components/NavigationSheet/NavigationSheet';
import WalletSheet from '../components/WalletSheet/WalletSheet';
import FabKebab from '../../../assets/icons/FabKebab';
import * as css from './TicketMenue.css';

function TicketMenue(): React.ReactElement | null {
    const {t} = useTranslation('eTicket');
    const {token} = useParams<{token: string}>();
    const data = useSelector((state: RootState) => state.eTicket.data);
    const [isOpen, setIsOpen] = useState(false);
    const contactShow = useShow();
    const navigateShow = useShow();
    const walletShow = useShow();
    const wrapRef = useRef<HTMLDivElement>(null);
    const isSubscription = useIsSubscription();

    useOnClickOutside(wrapRef, () => setIsOpen(false), isOpen);

    if(!data) return null;
    const openWallet = token && data.walletAllowed ? walletShow.open : undefined;
    // For a subscription the navigation target is the venue shared by all series events, not a single event
    const subscriptionVenues = isSubscription ? getSubscriptionVenues(data.tickets) : [];
    const menueItems = getFabMenu({data, t, openContact: contactShow.open, openNavigate: navigateShow.open, openWallet, isSubscription, subscriptionVenues});
    // address as well as location: the sheet reads address first, so leaving it would keep navigating
    // to the season's own address instead of the venue the series events share
    const navigateEvent = subscriptionVenues.length === 1
        ? {...data.event, address: subscriptionVenues[0], location: subscriptionVenues[0]}
        : data.event;

    const runAction = (onClick?: () => void) => {
        setIsOpen(false);
        onClick?.();
    };

    return (
        <>
            <css.scrim className={isOpen ? 'is-open' : ''} aria-hidden />
            <css.wrap
                ref={wrapRef}
                className={isOpen ? 'is-open' : ''}
                data-cy="ticket-fab"
            >
                <css.menu>
                    {menueItems.map(item => (
                        <MenuItem key={item.key} item={item} isOpen={isOpen} onRun={runAction} />
                    ))}
                </css.menu>
                <css.fab
                    type="button"
                    onClick={() => setIsOpen(open => !open)}
                    aria-expanded={isOpen}
                    aria-label={t('fabActions')}
                    data-cy="ticket-fab-toggle"
                >
                    <FabKebab size={26} />
                </css.fab>
            </css.wrap>
            <ContactSheet show={contactShow} data={data} />
            <NavigationSheet show={navigateShow} event={navigateEvent} />
            {token && data.walletAllowed && <WalletSheet show={walletShow} token={token} />}
        </>
    );
}

export default TicketMenue;
